import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  createGame,
  gravityInterval,
  hardDrop,
  holdPiece,
  isGrounded,
  lockActive,
  movePiece,
  pauseGame,
  rotatePiece,
  softDrop,
  stepDown,
  togglePause,
} from '../game/engine';
import { addGameProgress, readHighScore, recordGameStart, writeHighScore } from '../game/storage';
import type { GameModeId, GameState } from '../game/types';
import { bindingFromKeyboardEvent, type GameAction, type KeyBindings } from '../settings/settings';

const LOCK_DELAY_MS = 480;
const STATS_SAVE_DELAY_MS = 750;

interface SessionProgress {
  active: boolean;
  mode: GameModeId;
  score: number;
  lines: number;
  quads: number;
}

export interface GameControls {
  moveLeft: () => void;
  moveRight: () => void;
  softDrop: () => void;
  hardDrop: () => void;
  rotateClockwise: () => void;
  rotateCounterClockwise: () => void;
  hold: () => void;
  togglePause: () => void;
  startNewGame: (mode?: GameModeId) => void;
}

export function useGame(onRestartRequest: () => void, keyBindings: KeyBindings, keyboardEnabled = true): {
  game: GameState;
  highScore: number;
  statsRevision: number;
  controls: GameControls;
} {
  const [game, setGame] = useState<GameState>(() => createGame(Date.now(), 'idle'));
  const [highScore, setHighScore] = useState(() => readHighScore('regular'));
  const [statsRevision, setStatsRevision] = useState(0);
  const gameRef = useRef(game);
  const restartRequestRef = useRef(onRestartRequest);
  const gravityElapsed = useRef(0);
  const lockElapsed = useRef(0);
  const lastFrame = useRef<number | null>(null);
  const statsSaveTimer = useRef<number | null>(null);
  const sessionProgress = useRef<SessionProgress>({
    active: false,
    mode: 'regular',
    score: 0,
    lines: 0,
    quads: 0,
  });

  useEffect(() => {
    restartRequestRef.current = onRestartRequest;
  }, [onRestartRequest]);

  const commit = useCallback((next: GameState) => {
    if (next === gameRef.current) return false;
    gameRef.current = next;
    setGame(next);
    return true;
  }, []);

  const resetTimers = useCallback(() => {
    gravityElapsed.current = 0;
    lockElapsed.current = 0;
  }, []);

  const clearStatsSaveTimer = useCallback(() => {
    if (statsSaveTimer.current !== null) window.clearTimeout(statsSaveTimer.current);
    statsSaveTimer.current = null;
  }, []);

  const flushStats = useCallback((current: GameState) => {
    const session = sessionProgress.current;
    if (!session.active || session.mode !== current.mode) return;
    const score = Math.max(0, current.score - session.score);
    const lines = Math.max(0, current.lines - session.lines);
    const quads = Math.max(0, current.quads - session.quads);
    if (score > 0 || lines > 0 || quads > 0) {
      addGameProgress(current.mode, { score, lines, quads, bestLines: current.lines });
      setStatsRevision((revision) => revision + 1);
    }
    sessionProgress.current = {
      active: session.active,
      mode: current.mode,
      score: current.score,
      lines: current.lines,
      quads: current.quads,
    };
  }, []);

  const applyMovement = useCallback((action: (state: GameState) => GameState) => {
    const next = action(gameRef.current);
    if (commit(next)) lockElapsed.current = 0;
  }, [commit]);

  const startNewGame = useCallback((mode: GameModeId = gameRef.current.mode) => {
    clearStatsSaveTimer();
    flushStats(gameRef.current);
    sessionProgress.current.active = false;
    recordGameStart(mode);
    sessionProgress.current = { active: true, mode, score: 0, lines: 0, quads: 0 };
    setStatsRevision((revision) => revision + 1);
    resetTimers();
    setHighScore(readHighScore(mode));
    commit(createGame(Date.now(), 'playing', mode));
  }, [clearStatsSaveTimer, commit, flushStats, resetTimers]);

  const moveLeft = useCallback(() => applyMovement((state) => movePiece(state, -1)), [applyMovement]);
  const moveRight = useCallback(() => applyMovement((state) => movePiece(state, 1)), [applyMovement]);
  const dropSoft = useCallback(() => applyMovement(softDrop), [applyMovement]);
  const dropHard = useCallback(() => {
      resetTimers();
      commit(hardDrop(gameRef.current));
  }, [commit, resetTimers]);
  const rotateClockwise = useCallback(() => applyMovement((state) => rotatePiece(state, 1)), [applyMovement]);
  const rotateCounterClockwise = useCallback(() => applyMovement((state) => rotatePiece(state, -1)), [applyMovement]);
  const hold = useCallback(() => {
    lockElapsed.current = 0;
    commit(holdPiece(gameRef.current));
  }, [commit]);
  const pauseOrResume = useCallback(() => {
    resetTimers();
    commit(togglePause(gameRef.current));
  }, [commit, resetTimers]);

  const controls: GameControls = useMemo(() => ({
    moveLeft,
    moveRight,
    softDrop: dropSoft,
    hardDrop: dropHard,
    rotateClockwise,
    rotateCounterClockwise,
    hold,
    togglePause: pauseOrResume,
    startNewGame,
  }), [dropHard, dropSoft, hold, moveLeft, moveRight, pauseOrResume, rotateClockwise, rotateCounterClockwise, startNewGame]);

  useEffect(() => {
    if (game.score > highScore) {
      setHighScore(game.score);
      writeHighScore(game.score, game.mode);
    }
  }, [game.mode, game.score, highScore]);

  useEffect(() => {
    if (!sessionProgress.current.active || sessionProgress.current.mode !== game.mode) return;
    clearStatsSaveTimer();
    if (game.status === 'gameOver') {
      flushStats(gameRef.current);
      sessionProgress.current.active = false;
      return;
    }
    statsSaveTimer.current = window.setTimeout(() => flushStats(gameRef.current), STATS_SAVE_DELAY_MS);
    return clearStatsSaveTimer;
  }, [clearStatsSaveTimer, flushStats, game.lines, game.mode, game.quads, game.score, game.status]);

  useEffect(() => {
    const saveBeforeLeaving = () => {
      clearStatsSaveTimer();
      flushStats(gameRef.current);
    };
    window.addEventListener('pagehide', saveBeforeLeaving);
    return () => window.removeEventListener('pagehide', saveBeforeLeaving);
  }, [clearStatsSaveTimer, flushStats]);

  useEffect(() => {
    let frameId = 0;

    const frame = (now: number) => {
      if (lastFrame.current === null) lastFrame.current = now;
      const delta = Math.min(now - lastFrame.current, 100);
      lastFrame.current = now;
      let current = gameRef.current;

      if (current.status === 'playing' && current.active) {
        lockElapsed.current = isGrounded(current) ? lockElapsed.current + delta : 0;

        const interval = gravityInterval(current.level, current.mode);
        if (Number.isFinite(interval)) gravityElapsed.current += delta;
        if (Number.isFinite(interval) && gravityElapsed.current >= interval) {
          gravityElapsed.current %= interval;
          const stepped = stepDown(current);
          if (stepped !== current) {
            commit(stepped);
            current = stepped;
            lockElapsed.current = 0;
          }
        }

        if (isGrounded(current) && lockElapsed.current >= LOCK_DELAY_MS) {
          resetTimers();
          commit(lockActive(current));
        }
      }

      frameId = requestAnimationFrame(frame);
    };

    frameId = requestAnimationFrame(frame);
    return () => cancelAnimationFrame(frameId);
  }, [commit, resetTimers]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (!keyboardEnabled || event.isComposing) return;
      const binding = bindingFromKeyboardEvent(event);
      if (!binding) return;
      const action = (Object.entries(keyBindings) as [GameAction, string][]).find(([, value]) => value === binding)?.[0];
      if (!action) return;
      event.preventDefault();

      if (action === 'restart' && !event.repeat) {
        restartRequestRef.current();
        return;
      }
      if (action === 'pause' && !event.repeat) {
        controls.togglePause();
        return;
      }
      if (gameRef.current.status !== 'playing') return;

      const repeatedAction = action === 'moveLeft' || action === 'moveRight' || action === 'softDrop';
      if (event.repeat && !repeatedAction) return;

      if (action === 'moveLeft') controls.moveLeft();
      else if (action === 'moveRight') controls.moveRight();
      else if (action === 'softDrop') controls.softDrop();
      else if (action === 'rotateClockwise') controls.rotateClockwise();
      else if (action === 'rotateCounterClockwise') controls.rotateCounterClockwise();
      else if (action === 'hardDrop') controls.hardDrop();
      else if (action === 'hold') controls.hold();
    };

    window.addEventListener('keydown', onKeyDown, { passive: false });
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [controls, keyBindings, keyboardEnabled]);

  useEffect(() => {
    const pauseForFocus = () => {
      resetTimers();
      commit(pauseGame(gameRef.current, 'focus'));
    };
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') pauseForFocus();
    };

    window.addEventListener('blur', pauseForFocus);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('blur', pauseForFocus);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [commit, resetTimers]);

  return { game, highScore, statsRevision, controls };
}
